import React, { useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded'
import { NewIdentifierSubmission } from '../api/useIdentifierSubmissions'
import { advancedColors } from '../theme'
import { TAB_ICONS, TabIconKey } from '../dashboard/tabs'
import DialogHeader from './DialogHeader'

const LIBRARY_ICONS = Object.keys(TAB_ICONS) as TabIconKey[]

// A data URL in `localStorage`, so it must stay well inside a browser's 5–10MB origin quota.
const MAX_UPLOAD_BYTES = 300_000

type Props = {
  open: boolean
  onClose: () => void
  onSubmit: (input: NewIdentifierSubmission) => void
}

// Queues a request in `useIdentifierSubmissions` for review; nothing here reaches a patient's row.
const AddIdentifierDialog = ({ open, onClose, onSubmit }: Props) => {
  const [iconMode, setIconMode] = useState<'library' | 'upload'>('library')
  const [libraryIcon, setLibraryIcon] = useState<TabIconKey>(LIBRARY_ICONS[0])
  const [upload, setUpload] = useState<{ dataUrl: string; filename: string } | null>(null)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [detailedLogic, setDetailedLogic] = useState('')

  const reset = () => {
    setIconMode('library')
    setLibraryIcon(LIBRARY_ICONS[0])
    setUpload(null)
    setUploadError(null)
    setName('')
    setDescription('')
    setDetailedLogic('')
  }

  const close = () => {
    onClose()
    reset()
  }

  const onFileChosen = (file: File | undefined) => {
    setUploadError(null)
    if (!file) {
      return
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      setUploadError(`That file is too large — keep icons under ${Math.round(MAX_UPLOAD_BYTES / 1000)}KB.`)
      return
    }

    const reader = new FileReader()
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        setUpload({ dataUrl: reader.result, filename: file.name })
      }
    }
    reader.onerror = () => {
      setUploadError('Failed to read that file — try again or choose a different one.')
    }
    reader.readAsDataURL(file)
  }

  const canSubmit = name.trim() && description.trim() && detailedLogic.trim() && (iconMode === 'library' || upload)

  const submit = () => {
    if (!canSubmit) {
      return
    }
    onSubmit({
      name: name.trim(),
      description: description.trim(),
      detailedLogic: detailedLogic.trim(),
      icon: iconMode === 'library' ? { type: 'library', icon: libraryIcon } : { type: 'upload', ...upload! },
    })
    close()
  }

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
      <DialogHeader title="Add an identifier" onClose={close} />

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
        <Typography variant="body2" sx={{ color: advancedColors.inkMuted }}>
          This doesn&apos;t add anything to a patient&apos;s row yet — it sends a request to the product team, visible
          in the admin panel, to build it for real.
        </Typography>

        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Icon
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={iconMode}
            onChange={(_event, value: 'library' | 'upload' | null) => value && setIconMode(value)}
            sx={{ mb: 1.5 }}
          >
            <ToggleButton value="library">Choose from library</ToggleButton>
            <ToggleButton value="upload">Upload a file</ToggleButton>
          </ToggleButtonGroup>

          {iconMode === 'library' ? (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {LIBRARY_ICONS.map((key) => {
                const Icon = TAB_ICONS[key]
                const selected = key === libraryIcon

                return (
                  <IconButton
                    key={key}
                    aria-label={key}
                    onClick={() => setLibraryIcon(key)}
                    sx={{
                      border: `1px solid ${selected ? advancedColors.green : advancedColors.line}`,
                      backgroundColor: selected ? advancedColors.accentSoft : 'transparent',
                    }}
                  >
                    <Icon />
                  </IconButton>
                )
              })}
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Button
                variant="outlined"
                component="label"
                startIcon={<UploadFileRoundedIcon />}
                htmlFor="add-identifier-upload"
              >
                Choose file
                <input
                  id="add-identifier-upload"
                  type="file"
                  accept=".svg,.jpg,.jpeg,.png"
                  hidden
                  onChange={(event) => onFileChosen(event.target.files?.[0])}
                />
              </Button>
              {upload && <Typography variant="body2">{upload.filename}</Typography>}
            </Box>
          )}
          {uploadError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {uploadError}
            </Alert>
          )}
        </Box>

        <TextField label="Name" value={name} onChange={(event) => setName(event.target.value)} fullWidth />

        <TextField
          label="Short description"
          helperText="One or two sentences — what a coach should understand at a glance."
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          multiline
          minRows={2}
          fullWidth
        />

        <TextField
          label="Detailed logic"
          helperText="What should trigger this, for whom, and when should it clear? This is what the product team implements from."
          value={detailedLogic}
          onChange={(event) => setDetailedLogic(event.target.value)}
          multiline
          minRows={4}
          fullWidth
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={close}>Cancel</Button>
        <Button variant="contained" disabled={!canSubmit} onClick={submit}>
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default AddIdentifierDialog
